import { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';
import UserService from '../../services/user/user.service';

const ReferralPage = () => {
    const [referralLink, setReferralLink] = useState('');
    const [referralCode, setReferralCode] = useState('');
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const toast = useRef<Toast>(null);

    useEffect(() => {
        fetchReferralCode();
    }, []);

    const fetchReferralCode = async () => {
        try {
            setLoading(true);
            const userDetails = await UserService.getCurrentUser();

            if (userDetails && userDetails.userID) {
                // Create a simple encoded referral code (Base64 encode the userID)
                const code = btoa(`REF_${userDetails.userID}`);
                setReferralCode(code);

                // Generate the referral link
                const baseUrl = window.location.origin;
                const link = `${baseUrl}/register?ref=${code}`;
                setReferralLink(link);
            }
        } catch (error) {
            console.error('Error fetching referral code:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to generate referral link',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(referralLink).then(() => {
            setCopied(true);
            toast.current?.show({
                severity: 'success',
                summary: 'Copied!',
                detail: 'Referral link copied to clipboard',
                life: 3000
            });

            setTimeout(() => setCopied(false), 3000);
        });
    };

    const shareViaWhatsApp = () => {
        const message = encodeURIComponent(
            `🎁 Join Farm2Me and let's both earn rewards!\n\n` +
            `I'll earn rewards on EVERY order you make - and you get special benefits too! 🌟\n\n` +
            `Use my referral link to sign up:\n\n` +
            `${referralLink}\n\n` +
            `It's a win-win! The more you order, the more we both benefit! 💚`
        );
        window.open(`https://wa.me/?text=${message}`, '_blank');
    };

    const shareViaEmail = () => {
        const subject = encodeURIComponent('Join Farm2Me - Earn Rewards Together!');
        const body = encodeURIComponent(
            `Hi!\n\n` +
            `I'd like to invite you to join Farm2Me, an amazing platform for fresh farm products.\n\n` +
            `Here's the exciting part: When you use my referral link to sign up, we BOTH earn rewards! ` +
            `And I'll continue earning rewards on every order you make in the future. ` +
            `It's truly a win-win situation! 🎁\n\n` +
            `Your Referral Link:\n${referralLink}\n\n` +
            `Benefits for you:\n` +
            `✓ Fresh farm products delivered to your door\n` +
            `✓ Special rewards as a referred customer\n` +
            `✓ Quality products at great prices\n\n` +
            `Looking forward to having you on board!\n\n` +
            `Best regards`
        );
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    };

    return (
        <div className="p-2 md:p-4" style={{ backgroundColor: '#f9fafb', minHeight: '100vh' }}>
            <Toast ref={toast} />

            <div className="flex justify-content-center">
                <div style={{ maxWidth: '480px', width: '100%', marginTop: '1rem' }}>
                    {loading ? (
                        <div className="flex flex-column align-items-center justify-content-center p-8 text-center" style={{ background: '#fff', borderRadius: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)' }}>
                            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2.5rem', color: '#10b981' }}></i>
                            <p className="mt-4 text-600 font-medium">Preparing your rewards dashboard...</p>
                        </div>
                    ) : (
                        <div className="flex flex-column gap-4">
                            {/* Header Section */}
                            <div className="text-center p-4">
                                <div
                                    className="inline-flex align-items-center justify-content-center mb-3"
                                    style={{
                                        width: '64px',
                                        height: '64px',
                                        borderRadius: '20px',
                                        background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                                        color: '#059669',
                                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)'
                                    }}
                                >
                                    <i className="pi pi-gift" style={{ fontSize: '2.5rem' }}></i>
                                </div>
                                <h2 style={{ color: '#065f46', margin: '0 0 0.5rem 0', fontSize: '1.75rem', fontWeight: '800' }}>Refer & Earn</h2>
                                <p style={{ color: '#6b7280', margin: 0, fontSize: '1rem', lineHeight: '1.5' }}>
                                    Invite your friends and earn rewards on <span className="font-bold text-emerald-600" style={{ color: '#059669' }}>every order</span> they make!
                                </p>
                            </div>

                            {/* Referral Link Card */}
                            <div
                                className="p-4"
                                style={{
                                    background: '#fff',
                                    borderRadius: '24px',
                                    boxShadow: '0 20px 40px rgba(0,0,0,0.04)',
                                    border: '1px solid #f3f4f6'
                                }}
                            >
                                <label className="block mb-3 text-sm font-bold text-700 uppercase tracking-wider" style={{ color: '#374151' }}>
                                    Your Referral Link
                                </label>
                                <div className="flex align-items-center p-3 mb-4" style={{ borderRadius: '16px', border: '2px solid #ecfdf5', background: '#f9fafb' }}>
                                    <div className="flex-1 overflow-hidden" style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#065f46', fontWeight: '500' }}>
                                        {referralLink}
                                    </div>
                                    <Button
                                        icon={copied ? "pi pi-check" : "pi pi-copy"}
                                        onClick={copyToClipboard}
                                        text
                                        rounded
                                        severity={copied ? "success" : "secondary"}
                                        className="ml-2"
                                        style={{ color: copied ? '#10b981' : '#6b7280' }}
                                    />
                                </div>

                                <div className="flex flex-column gap-3">
                                    <Button
                                        label="Share via WhatsApp"
                                        icon="pi pi-whatsapp"
                                        onClick={shareViaWhatsApp}
                                        className="w-full py-3"
                                        style={{
                                            background: '#25D366',
                                            borderColor: '#25D366',
                                            borderRadius: '16px',
                                            fontSize: '1.1rem',
                                            fontWeight: '700',
                                            boxShadow: '0 10px 20px rgba(37, 211, 102, 0.2)'
                                        }}
                                    />
                                    <Button
                                        label="Other Options"
                                        icon="pi pi-share-alt"
                                        onClick={shareViaEmail}
                                        text
                                        className="w-full"
                                        style={{ color: '#6b7280', fontWeight: '600' }}
                                    />
                                </div>
                            </div>

                            {/* Reward Highlight */}
                            <div
                                className="p-3 flex align-items-center gap-3"
                                style={{
                                    background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                                    borderRadius: '20px',
                                    border: '1px solid #fde68a'
                                }}
                            >
                                <div className="p-2" style={{ background: '#fbbf24', borderRadius: '12px' }}>
                                    <i className="pi pi-star-fill" style={{ color: '#fff', fontSize: '1.2rem' }}></i>
                                </div>
                                <div>
                                    <p className="m-0 font-bold" style={{ color: '#92400e', fontSize: '0.95rem' }}>Earning Potential: Unlimited!</p>
                                    <p className="m-0 text-sm" style={{ color: '#b45309' }}>The more they order, the more you win.</p>
                                </div>
                            </div>

                            {/* Simplified How it works */}
                            <div className="mt-2 text-center">
                                <p className="text-sm font-bold uppercase tracking-widest text-400 mb-4" style={{ color: '#9ca3af' }}>How it works</p>
                                <div className="flex justify-content-between px-2">
                                    <div className="flex flex-column align-items-center gap-2" style={{ width: '30%' }}>
                                        <div className="w-3rem h-3rem border-circle flex align-items-center justify-content-center" style={{ background: '#ecfdf5', color: '#059669' }}>
                                            <i className="pi pi-send"></i>
                                        </div>
                                        <span className="text-xs font-semibold text-600">Send Link</span>
                                    </div>
                                    <div className="flex flex-column align-items-center gap-2" style={{ width: '30%' }}>
                                        <div className="w-3rem h-3rem border-circle flex align-items-center justify-content-center" style={{ background: '#ecfdf5', color: '#059669' }}>
                                            <i className="pi pi-user-plus"></i>
                                        </div>
                                        <span className="text-xs font-semibold text-600">They Join</span>
                                    </div>
                                    <div className="flex flex-column align-items-center gap-2" style={{ width: '30%' }}>
                                        <div className="w-3rem h-3rem border-circle flex align-items-center justify-content-center" style={{ background: '#ecfdf5', color: '#059669' }}>
                                            <i className="pi pi-wallet"></i>
                                        </div>
                                        <span className="text-xs font-semibold text-600">Earn Rewards</span>
                                    </div>
                                </div>
                            </div>

                            <p className="text-center text-xs mt-4 px-4" style={{ color: '#9ca3af', lineHeight: '1.6' }}>
                                *Rewards are applicable only for new customer referrals. Terms and conditions apply.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReferralPage;
